import {ActivatedRouteSnapshot, CanActivateFn, CanDeactivateFn, Router, UrlTree} from "@angular/router";
import {Inject, inject, PLATFORM_ID} from "@angular/core";
import {catchError, map, Observable, of, switchMap, take, tap} from "rxjs";
import {
  EVENT_CALENDAR_ROUTE,
  PAYMENT
} from "src/app/app-routing.module";
import { HttpClient } from "@angular/common/http";
import {JwtResponse, TokenRefreshRequest, TokenRefreshResponse} from "@model";
import {AuthenticationService} from "./authentication.service";
import {JWT_RESPONSE, LocalStorageService} from "./local-storage.service";
import {LoaderService} from "./loader.service";
import {UserDetailService} from "./user-detail.service";
import {PermissionService} from "./permission.service";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";
import {EnvironmentService} from "./environment.service";

export function canActivateAuthenticationGuard(): CanActivateFn {
  return (route: ActivatedRouteSnapshot) => {
    let authService: AuthenticationService = inject(AuthenticationService);
    let localStorageService: LocalStorageService = inject(LocalStorageService);
    let router: Router = inject(Router);
    let permissionService: PermissionService = inject(PermissionService);
    let userService: UserDetailService = inject(UserDetailService);
    let loaderService: LoaderService = inject(LoaderService);
    let document: Document = inject(DOCUMENT);
    let environmentService: EnvironmentService = inject(EnvironmentService);
    let http: HttpClient = inject(HttpClient);
    let platformId: Object = inject(PLATFORM_ID);

    var wsPaymentRoute: boolean = isWsPaymentRoute(document);

    return authService.jwtResponse.pipe(take(1), switchMap((jwtResponse: JwtResponse | null) => {
      const isAuthenticated: boolean = !!jwtResponse;
      if (isAuthenticated || wsPaymentRoute) {
        return of(true);
      } else {
        return autoLogin(wsPaymentRoute, localStorageService, router, authService, permissionService, userService,
          loaderService, environmentService, platformId, http);
      }
    }));
  }
}

export const autoLoginOnLeaveGuard: CanDeactivateFn<any> = (component) => {
  let authService: AuthenticationService = inject(AuthenticationService);
  let localStorageService: LocalStorageService = inject(LocalStorageService);
  let router: Router = inject(Router);
  let permissionService: PermissionService = inject(PermissionService);
  let userService: UserDetailService = inject(UserDetailService);
  let loaderService: LoaderService = inject(LoaderService);
  let document: Document = inject(DOCUMENT);
  let environmentService: EnvironmentService = inject(EnvironmentService);
  let http: HttpClient = inject(HttpClient);
  let platformId: Object = inject(PLATFORM_ID);

  if ((component as any).__autoLoginExecuted) {
    return true;
  }
  (component as any).__autoLoginExecuted = true;
  autoLogin(isWsPaymentRoute(document), localStorageService, router, authService, permissionService, userService, loaderService,
      environmentService, platformId, http).pipe(take(1)).subscribe();

  return true;
};


function autoLogin(isWsPaymentRoute: boolean, localStorageService: LocalStorageService, router: Router,
                   authService: AuthenticationService,
                   permissionService: PermissionService,
                   userService: UserDetailService,
                   loaderService: LoaderService,
                   environmentService:EnvironmentService,
                   platformId: Object,
                   http: HttpClient): Observable<boolean | UrlTree> {
  let defaultReturnValue: Observable<boolean> | Observable<UrlTree> = isWsPaymentRoute ? of(true) : of(router.createUrlTree([EVENT_CALENDAR_ROUTE]));
  if (!isPlatformBrowser(platformId)) {
    return defaultReturnValue;
  }

  let data: any = localStorageService.getData(JWT_RESPONSE);
  if (data) {
    try {
      let jwtResponse: JwtResponse = JSON.parse(data);
      if (jwtResponse.refreshToken) {
        return refreshJwtToken(jwtResponse, new TokenRefreshRequest(jwtResponse.refreshToken!, jwtResponse.username, jwtResponse.email, jwtResponse.token), defaultReturnValue,
          authService, permissionService, localStorageService, userService, environmentService, loaderService, http);
      } else {
        return defaultReturnValue;
      }
    } catch (e) {
      return defaultReturnValue;
    }
  } else {
    return defaultReturnValue;
  }
}

function isWsPaymentRoute(doc: Document): boolean {
  const path = doc.location.href;
  return !!path && (
      path.includes(PAYMENT)
  );
}


function refreshJwtToken(jwtResponse: JwtResponse, tokenRefreshRequest: TokenRefreshRequest,
                         defaultReturnValue: Observable<boolean> | Observable<UrlTree>,
                         authService: AuthenticationService, permissionService: PermissionService,
                         localStorageService: LocalStorageService, userService: UserDetailService,
                         environmentService:EnvironmentService,
                         loaderService: LoaderService, http: HttpClient): Observable<boolean | UrlTree> {
  loaderService.startLoading();
  return http.post<TokenRefreshResponse>(environmentService.getApiUrl() + "/api/auth/refreshtoken", tokenRefreshRequest)
    .pipe((
        tap(() => loaderService.dismissLoading())),
      map((result: TokenRefreshResponse) => {
        jwtResponse.refreshToken = result.refreshToken;
        jwtResponse.token = result.accessToken;
        authService.jwtResponse.next(jwtResponse);
        localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
        userService.fetchUserProfile();
        return true;
      }), catchError(error => {
        localStorageService.removeData(JWT_RESPONSE);
        permissionService.emptyPermissions();
        return defaultReturnValue;
      }));
}
